import React, { useState } from 'react';
import { Copy, Check, Download, Palette, Type, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/button';

export default function PowerPointInstructions({ instructions, assets, slideNumber }) {
  const [copiedStep, setCopiedStep] = useState(null);

  const handleCopyStep = (stepNumber, text) => {
    navigator.clipboard.writeText(text);
    setCopiedStep(stepNumber);
    setTimeout(() => setCopiedStep(null), 2000);
  };

  const handleCopyAllInstructions = () => {
    const allText = instructions
      .map(
        (inst) =>
          `${inst.step_number}. ${inst.action}\n   Menu: ${inst.menu_path}\n   ${inst.details}\n`
      )
      .join('\n');
    navigator.clipboard.writeText(allText);
    setCopiedStep('all');
    setTimeout(() => setCopiedStep(null), 2000);
  };

  const handleDownloadAssets = () => {
    const assetsText = JSON.stringify(assets, null, 2);
    const blob = new Blob([assetsText], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `slide-${slideNumber}-assets.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-lg border-2 border-purple-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-100 to-pink-100 px-6 py-4 border-b border-purple-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-purple-900">
            PowerPoint Instructions
          </h3>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyAllInstructions}
              className="flex items-center space-x-1 border-purple-300 hover:bg-purple-50"
            >
              {copiedStep === 'all' ? (
                <>
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-green-600">Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span>Copy All</span>
                </>
              )}
            </Button>
            {(assets.colors?.length > 0 || assets.fonts?.length > 0 || assets.images?.length > 0) && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadAssets}
                className="flex items-center space-x-1 border-purple-300 hover:bg-purple-50"
              >
                <Download className="w-4 h-4" />
                <span>Assets</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Instructions List */}
      <div className="p-6 space-y-4 max-h-[600px] overflow-y-auto">
        {instructions && instructions.length > 0 ? (
          instructions.map((instruction) => (
            <div
              key={instruction.step_number}
              className="bg-slate-50 rounded-lg p-4 border border-slate-200 hover:border-purple-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-semibold text-sm">
                      {instruction.step_number}
                    </div>
                    <h4 className="font-semibold text-slate-900 text-base">
                      {instruction.action}
                    </h4>
                  </div>
                  <div className="ml-11">
                    <div className="bg-purple-50 rounded px-3 py-2 mb-2 border border-purple-200">
                      <p className="text-sm font-mono text-purple-900">
                        {instruction.menu_path}
                      </p>
                    </div>
                    <p className="text-slate-700 text-sm leading-relaxed">
                      {instruction.details}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    handleCopyStep(
                      instruction.step_number,
                      `${instruction.step_number}. ${instruction.action}\nMenu: ${instruction.menu_path}\n${instruction.details}`
                    )
                  }
                  className="ml-2 flex-shrink-0"
                >
                  {copiedStep === instruction.step_number ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4 text-slate-400" />
                  )}
                </Button>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-slate-500">
            No instructions available
          </div>
        )}
      </div>

      {/* Assets Section */}
      {(assets.colors?.length > 0 || assets.fonts?.length > 0 || assets.images?.length > 0) && (
        <div className="border-t border-purple-200 bg-slate-50 p-6">
          <h4 className="text-base font-semibold text-slate-900 mb-4">
            Assets Needed
          </h4>

          <div className="space-y-4">
            {/* Colors */}
            {assets.colors && assets.colors.length > 0 && (
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <Palette className="w-4 h-4 text-purple-600" />
                  <h5 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                    Colors
                  </h5>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {assets.colors.map((color, idx) => (
                    <div
                      key={idx}
                      className="flex items-center space-x-3 bg-white rounded-lg p-3 border border-slate-200"
                    >
                      <div
                        className="w-10 h-10 rounded-lg border-2 border-slate-300 flex-shrink-0"
                        style={{ backgroundColor: color.hex }}
                        title={color.hex}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 text-sm truncate">
                          {color.name}
                        </p>
                        <div className="flex items-center space-x-2">
                          <p className="text-xs text-slate-600 font-mono">{color.hex}</p>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(color.hex);
                              setCopiedStep(`color-${idx}`);
                              setTimeout(() => setCopiedStep(null), 1000);
                            }}
                            className="text-slate-400 hover:text-slate-600"
                          >
                            {copiedStep === `color-${idx}` ? (
                              <Check className="w-3 h-3 text-green-600" />
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                          </button>
                        </div>
                        <p className="text-xs text-slate-500 truncate">{color.usage}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fonts */}
            {assets.fonts && assets.fonts.length > 0 && (
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <Type className="w-4 h-4 text-purple-600" />
                  <h5 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                    Fonts
                  </h5>
                </div>
                <div className="space-y-2">
                  {assets.fonts.map((font, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between bg-white rounded-lg p-3 border border-slate-200"
                    >
                      <div>
                        <p className="font-semibold text-slate-900 text-sm">
                          {font.name} {font.size && `${font.size}pt`}
                        </p>
                        <p className="text-xs text-slate-500">{font.usage}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Images */}
            {assets.images && assets.images.length > 0 && (
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <ImageIcon className="w-4 h-4 text-purple-600" />
                  <h5 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                    Images
                  </h5>
                </div>
                <div className="space-y-2">
                  {assets.images.map((image, idx) => (
                    <div
                      key={idx}
                      className="bg-white rounded-lg p-3 border border-slate-200"
                    >
                      <p className="font-semibold text-slate-900 text-sm mb-1">
                        {image.description}
                      </p>
                      <div className="flex items-center space-x-4 text-xs text-slate-600">
                        {image.dimensions && <span>Size: {image.dimensions}</span>}
                        {image.placement && <span>• {image.placement}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
